import React, { useEffect, useRef } from "react";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
  WorkflowName,
} from "../../../apis/API";
import { lookupWorkflow } from "./workflowRegistry";
import YesNoFreeTextForm from "./YesNoFreeTextForm";
import ConfirmOnFileForm from "./ConfirmOnFileForm";
import StructuredForm from "./StructuredForm";
import EmploymentForm from "./EmploymentForm";
import RepeatableListForm from "./RepeatableListForm";

/**
 * Answer payload emitted by a workflow question (subset of
 * IdentifiableAnswerV3Input). Discriminated on `skipped`:
 * - skipped: unsupported/unknown workflow — carries no workflowName (matching
 *   the Android contract, which never echoes an unknown name).
 * - answered: carries the workflowName, the root branch answer (null for
 *   ALWAYS templates), and the serialized `workflowAnswers` JSON.
 */
export type WorkflowAnswer =
  | { questionId: string; questionType: QuestionType.workflow; skipped: true }
  | {
      questionId: string;
      questionType: QuestionType.workflow;
      skipped?: false;
      workflowName: WorkflowName;
      answer?: boolean | null;
      /** Serialized AWSJSON `workflowAnswers`. */
      workflowAnswers: string;
    };

export interface WorkflowQuestionProps {
  question: LocalizedParticipantQuestion;
  /**
   * Raw `questionPrepopulation.values` JSON for this workflow: on-file data for
   * CONFIRM_ON_FILE / employment, or the last submitted answer for the
   * history-prefill workflows (see `historyPrefill.ts`).
   */
  prepopulationValues?: string;
  /** Prior answer, for back-nav re-display (forwarded to the template form). */
  defaultAnswer?: IdentifiableAnswerV3Input;
  onAnswer: (answer: WorkflowAnswer) => void;
}

/**
 * Workflow question dispatcher.
 *
 * Resolves the question's `workflowName` to a spec and routes to the template's
 * UI. Unknown/unsupported workflows are skipped — the dispatcher emits
 * `{ skipped: true }` (no workflowName) and renders nothing, so the flow engine
 * (IHA-347) can advance past it; the question is excluded from completion.
 *
 * All templates that any registered workflow uses have a form: `YES_NO_FREE_TEXT`
 * (IHA-366), `CONFIRM_ON_FILE` (IHA-363), `YES_NO_STRUCTURED_FORM` (IHA-364), and
 * `YES_NO_REPEATABLE_LIST` / `DIRECT_LIST` (IHA-365). An unknown workflow OR an
 * unhandled template (e.g. `FREE_TEXT_ONLY`, which has no registered workflows)
 * is treated the same — it emits `{ skipped: true }` and renders nothing, so the
 * flow engine advances rather than stalling on a blank screen.
 */
const WorkflowQuestion: React.FC<WorkflowQuestionProps> = ({
  question,
  prepopulationValues,
  defaultAnswer,
  onAnswer,
}) => {
  const spec = lookupWorkflow(question.workflowName);
  const questionId = question.id ?? "";

  // A registered workflow whose template has an implemented form. Unknown
  // workflows (spec null) AND any unhandled template (e.g. FREE_TEXT_ONLY, which
  // has no registered workflows) are treated the same: skipped, so the flow
  // engine advances rather than stalling on a blank screen.
  const hasForm =
    spec !== null &&
    (spec.template === "YES_NO_FREE_TEXT" ||
      spec.template === "CONFIRM_ON_FILE" ||
      spec.template === "YES_NO_STRUCTURED_FORM" ||
      spec.template === "YES_NO_REPEATABLE_LIST" ||
      spec.template === "DIRECT_LIST");

  // Keep onAnswer out of the effect deps so a new callback identity per render
  // never re-fires the skip emission.
  const onAnswerRef = useRef(onAnswer);
  onAnswerRef.current = onAnswer;

  useEffect(() => {
    if (!hasForm) {
      onAnswerRef.current({
        questionId,
        questionType: QuestionType.workflow,
        skipped: true,
      });
    }
  }, [questionId, hasForm]);

  if (spec === null || !hasForm) {
    return null;
  }

  // spec is non-null here, so the workflowName is a registered one.
  // YES_NO_FREE_TEXT (IHA-366): a Yes/No → "Provide Details" form.
  if (spec.template === "YES_NO_FREE_TEXT") {
    return (
      <YesNoFreeTextForm
        question={question}
        workflowName={question.workflowName as WorkflowName}
        spec={spec}
        prepopulationValues={prepopulationValues}
        defaultAnswer={defaultAnswer}
        onAnswer={onAnswer}
      />
    );
  }

  // CONFIRM_ON_FILE (IHA-363): on-file card → Correct/Changed (+ edit form).
  if (spec.template === "CONFIRM_ON_FILE") {
    return (
      <ConfirmOnFileForm
        question={question}
        workflowName={question.workflowName as WorkflowName}
        spec={spec}
        prepopulationValues={prepopulationValues}
        defaultAnswer={defaultAnswer}
        onAnswer={onAnswer}
      />
    );
  }

  // Employment (YES_NO_STRUCTURED_FORM + `onFileListConfirm`): the reworked
  // on-file-list-confirm screen — loads on-file jobs and asks "Have there been
  // changes?" before the entry form. Dispatched on the spec capability (not a
  // name check), mirroring Android's dedicated EmploymentEditor.
  if (spec.template === "YES_NO_STRUCTURED_FORM" && spec.onFileListConfirm) {
    return (
      <EmploymentForm
        question={question}
        workflowName={question.workflowName as WorkflowName}
        spec={spec}
        prepopulationValues={prepopulationValues}
        defaultAnswer={defaultAnswer}
        onAnswer={onAnswer}
      />
    );
  }

  // YES_NO_STRUCTURED_FORM (IHA-364): Yes/No → multi-field form (repeatable for
  // vehicles); ALWAYS variant (officerReporting) shows the form immediately.
  if (spec.template === "YES_NO_STRUCTURED_FORM") {
    return (
      <StructuredForm
        question={question}
        workflowName={question.workflowName as WorkflowName}
        spec={spec}
        prepopulationValues={prepopulationValues}
        defaultAnswer={defaultAnswer}
        onAnswer={onAnswer}
      />
    );
  }

  // YES_NO_REPEATABLE_LIST + DIRECT_LIST (IHA-365): a single-field list with
  // "Add another". These are the only templates left after the `hasForm` guard,
  // so any spec reaching here is a list (DIRECT_LIST/householdMembers shows
  // immediately with a skip). RepeatableListForm handles both variants.
  return (
    <RepeatableListForm
      question={question}
      workflowName={question.workflowName as WorkflowName}
      spec={spec}
      prepopulationValues={prepopulationValues}
      defaultAnswer={defaultAnswer}
      onAnswer={onAnswer}
    />
  );
};

export default WorkflowQuestion;
